import { useOffsetTopForElId } from '@glow/common'
import { tUnchecked } from '@glow/language'
import classNames from 'classnames'
import { t } from 'i18next'
import { ReactNode, useId, useState } from 'react'
import { ButtonBase, Card, IconButton, Text } from '../../atoms'
import type { IconName } from '../../atoms/icon'
import { SearchField } from '../../molecules'
import styles from './index.module.css'

type IdType = string | number
interface Item {
  id: IdType
  name?: ReactNode
  description?: ReactNode
  displayGroup?: boolean
  secondRow?: ReactNode
}

export const SelectedCards = ({
  titleKey,
  items,
  emptyStateKey,
  searchPhrase,
  count,
  secondIcon,
  setSearchPhrase,
  onRemoveRow,
  onClearAll
}: {
  titleKey: string
  items: Array<Item>
  emptyStateKey?: string
  searchPhrase: string
  count: number
  secondIcon?: { icon: IconName; label: string; onClick: (id: IdType) => void }
  setSearchPhrase: (value: string) => void
  onRemoveRow: (id: IdType) => void
  onClearAll: () => void
}) => {
  const elScrollId = useId()
  const [scrollElOffsetTop] = useOffsetTopForElId(elScrollId)
  return (
    <div>
      <div className="flex justify-between">
        <Text as="h2" textType="lg">
          {tUnchecked(titleKey, { count })}
        </Text>
        <ButtonBase type="button" onClick={onClearAll} small variant="secondary">
          {t('glow.clearAll')}
        </ButtonBase>
      </div>
      <SearchField searchPhrase={searchPhrase} setSearchPhrase={setSearchPhrase} />
      <div
        id={elScrollId}
        style={{ maxHeight: `calc(100vh - ${scrollElOffsetTop}px - 16px -  32px)` }}
        className="overflow-y-auto overflow-x-hidden pb-4 px-2 -mx-2 max-h-[60vh]"
      >
        {items.length === 0 ? (
          <Text as="div" textType="base" className="text-emphasis-medium mt-4 italic">
            {tUnchecked(emptyStateKey ?? 'glow.emptyStates.notFoundTitle')}
          </Text>
        ) : (
          items.map((item, index) => (
            <SelectedCard
              key={item.id + index.toString()}
              secondIcon={secondIcon}
              onRemoveRow={onRemoveRow}
              item={item}
            />
          ))
        )}
      </div>
    </div>
  )
}

const SelectedCard = ({
  secondIcon,
  item,
  onRemoveRow
}: {
  item: Item
  secondIcon?: { icon: IconName; label: string; onClick: (id: IdType) => void }
  onRemoveRow: (id: IdType) => void
}) => {
  const [effect, setEffect] = useState(false)
  return (
    <Card
      onAnimationEnd={() => {
        setEffect(false)
        onRemoveRow(item.id)
      }}
      className={classNames(
        styles.card,
        effect && styles.animation,
        secondIcon ? 'grid-cols-[auto_72px]' : 'grid-cols-[auto_32px]',
        !item.secondRow && 'py-2'
      )}
    >
      <div>
        <div>
          <Text as="span" textType="sm-black" semiBold className="mr-4">
            {item.name}
          </Text>
          {item.description ? (
            <Text as="span" textType="sm">
              {item.description}
            </Text>
          ) : item.displayGroup ? (
            <Text as="span" textType="sm">
              {t('glow.group')}
            </Text>
          ) : null}
        </div>
        {item.secondRow && <div className="mt-2">{item.secondRow}</div>}
      </div>

      <div className="flex gap-2 self-start">
        {secondIcon && (
          <IconButton
            size="sm"
            icon={secondIcon.icon}
            aria-label={secondIcon.label}
            onClick={() => secondIcon.onClick(item.id)}
          />
        )}
        <IconButton
          size="sm"
          icon="trash-alt"
          aria-label={'Remove card with name ' + item.name}
          onClick={() => {
            setEffect(true)
          }}
        />
      </div>
    </Card>
  )
}
